import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import * as Collapsible from '@radix-ui/react-collapsible'

import { useOnEvent } from '@sketch/utils'
import {
  useModalContext,
  SelectDropdownActionDropdown,
} from '@sketch/components'
import { DroppableLinkItem, RouteParams } from '@sketch/modules-common'
import { ConfirmStorageItemTransferModalDragged } from './ConfirmStorageItemTransferModalDragged'

import { RenameInlineEditor } from '../InlineEditor'
import ProjectDropdown from '../ProjectDropdown'

import {
  CollapsibleButton,
  ChevronRight,
  ChevronDown,
  NestedProjectsWrapper,
  TouchOnlyCollapsibleIcon,
  TouchOnlyCollapsibleWrapper,
  PushPin,
  IconWithPinWrapper,
  PushPinShadow,
} from './ProjectListItem.styles'

import { ReactComponent as MyDraftsIcon } from '@sketch/icons/notes-17'
import { ReactComponent as FolderClosedIcon } from '@sketch/icons/folder-closed-17'
import { ReactComponent as FolderOpenIcon } from '@sketch/icons/folder-open-17'

import { ProjectProps } from './ProjectListItem.types'

import { privacyIcon, hasProject } from './ProjectListItem.utils'

import { isMyDrafts } from '../../utils'
import { StorageItemFragment } from '@sketch/gql-types'
import { useTransfer } from './ProjectListItem.hooks'
import { useSelectionContext } from '../../../storageItems/components/SelectionContext'

export const ProjectListItem: React.FCC<ProjectProps> = props => {
  const {
    link,
    project,
    nestedProjects,
    workspace,
    refToLast,
    setEditedItem,
    forceHighlight = false,
    isProjectList,
    onOpenChange,
    defaultOpen,
    depth = 0,
    topLevelProject,
    isDescendantOf,
  }: typeof props = props

  const { showModal } = useModalContext()
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)
  const { clearSelection } = useSelectionContext()

  const { projectId: openedProject } =
    useParams<RouteParams<'WORKSPACE_PROJECT'>>()

  // An active nested projects is expanded by default
  const [nestedOpen, _setNestedOpen] = React.useState(
    hasProject(nestedProjects, openedProject) || defaultOpen(project.identifier)
  )

  const setNestedOpen = useCallback(
    (open: boolean) => {
      _setNestedOpen(open)
      onOpenChange(project.identifier, open)
    },
    [onOpenChange, project.identifier]
  )

  const excludeInvalidDragItems = useCallback(
    (draggedItems: StorageItemFragment[]) => {
      const validItems: StorageItemFragment[] = []

      draggedItems.forEach(item => {
        if (
          item.__typename === 'Project' &&
          // We cannot drop a project into itself
          item.identifier !== project.identifier &&
          // We cannot drop a project into his parent (maintaining the same relation as currently)
          item.parentProjectIdentifier !== project.identifier &&
          // We cannot drop a project into one of its descendants
          !isDescendantOf(item.identifier, project.identifier)
        ) {
          validItems.push(item)
        }

        if (
          item.__typename === 'Share' &&
          item.project?.identifier !== project.identifier
        ) {
          validItems.push(item)
        }
      })

      return validItems
    },
    [project, isDescendantOf]
  )

  const location = useLocation()

  // Let's make sure we show the active project item when the route changes
  useEffect(() => {
    if (hasProject(nestedProjects, openedProject)) {
      setNestedOpen(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // We want to show expanded newly created nested projects
  useOnEvent('nestedProjectCreated', ({ projectId }) => {
    if (hasProject(nestedProjects, projectId)) {
      setNestedOpen(true)
    }
  })

  const { move } = useTransfer({
    workspaceId: workspace.identifier,
    newProject: project,
  })

  const onStorageItemsDragOver = (
    draggedElement: StorageItemFragment,
    relatableItems: StorageItemFragment[]
  ) => {
    const validItems = excludeInvalidDragItems(relatableItems)
    setIsDroppableAreaEnabled(validItems.length > 0)
  }

  const onStorageItemsDropped = (
    draggedElement: StorageItemFragment,
    relatableItems: StorageItemFragment[]
  ) => {
    const validItems = excludeInvalidDragItems(relatableItems)

    /**
     *  If there are no valid item to move we halt the operation
     *  otherwise we are being permissive and allowing the rest to be moved
     */
    if (validItems.length === 0) {
      return
    }

    /**
     *If the folder shares the same top level project
     * we can move the folder without any confirmation
     * as the sharing permissions are the same
     */
    const noConfirmationNeeded = validItems.every(item => {
      const itemProject = item.__typename === 'Project' ? item : item.project

      return (
        (itemProject?.topLevelProject || itemProject)?.identifier ===
        (project.topLevelProject || project)?.identifier
      )
    })

    if (noConfirmationNeeded) {
      move(validItems, clearSelection)
      return
    }

    showModal(ConfirmStorageItemTransferModalDragged, {
      items: validItems,
      workspace,
      destinationProject: project,
      onTransferComplete: clearSelection,
    })
  }

  const workspaceId = workspace.identifier

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem?.(project.identifier)
  }

  // We should show the pin icon when FF is enabled
  // when the project is pinned and when it's not
  // a private project, as the lock icon should prevail
  // https://www.sketch.com/s/d57ba117-58de-4a49-b217-0720404f0a08/a/oM73ayj
  const showPinIcon =
    depth === 0 && !isProjectList && !!project.pinnedByCurrentUserAt

  // This function decides which icon needs to be shown for the project
  const getIcon = () => {
    const nestedProjectIcons = {
      ...(nestedProjects.length && {
        hoverIcon: nestedOpen ? ChevronDown : ChevronRight,
      }),
      ...(nestedProjects.length && {
        hoverIconActive: nestedOpen ? ChevronDown : ChevronRight,
      }),
    }

    if (project.projectType === 'PERSONAL_DRAFTS') {
      if (project.parentProjectIdentifier) {
        const icon = nestedOpen ? FolderOpenIcon : FolderClosedIcon
        return {
          icon,
          activeIcon: icon,
          label: 'My Drafts',
          ...nestedProjectIcons,
        }
      }
      return {
        icon: MyDraftsIcon,
        activeIcon: MyDraftsIcon,
        label: 'My Drafts',
        ...nestedProjectIcons,
      }
    }

    // Sharing settings can be set only on the top level project and all nested projects inherit the same settings
    // Therefore privacy icons should be always based on the top level project
    // See: https://linear.app/sketch/issue/SWEB-1490/sidebar-icon-updates-on-top-folder-but-not-on-nested-folders
    const icon = privacyIcon(topLevelProject || project, nestedOpen)

    const IconWithPin = ({ className }: any) => {
      const Icon = icon.component
      return (
        <IconWithPinWrapper className={className}>
          <Icon />
          <PushPinShadow />
          <PushPin />
        </IconWithPinWrapper>
      )
    }

    return {
      icon: showPinIcon ? IconWithPin : icon.component,
      activeIcon: showPinIcon ? IconWithPin : icon.component,
      label: icon.label,
      ...nestedProjectIcons,
    }
  }

  const Trigger: React.FCC = ({ children }) => (
    <Collapsible.Trigger asChild>
      <CollapsibleButton>{children}</CollapsibleButton>
    </Collapsible.Trigger>
  )
  const icon = getIcon()

  const hasChildren = nestedProjects.length > 0

  // The only project list item that shouldn't be draggable in the sidebar is
  // the "My Drafts" project, as it is a special project
  const shouldBeDraggable =
    !isMyDrafts(project) &&
    (workspace.userCanEdit || workspace.userCanAdminister)

  const linkItem = isRenaming ? (
    <RenameInlineEditor
      depth={depth + 1}
      project={project}
      workspaceId={workspaceId}
      placeholder={project.name || 'Folder'}
      toggleEditor={toggleEditor}
      onCompleted={onCompleted}
    />
  ) : (
    <DroppableLinkItem
      draggable={shouldBeDraggable}
      projectIdentifier={project.identifier}
      isDroppableAreaEnabled={isDroppableAreaEnabled}
      icon={icon}
      iconLabel={icon.label}
      {...(hasChildren && { iconWrapper: Trigger })}
      to={link}
      text={project.name}
      onStorageItemsDragOver={onStorageItemsDragOver}
      onStorageItemsDropped={onStorageItemsDropped}
      onUnknownDragOver={() => setIsDroppableAreaEnabled(false)}
      action={
        <SelectDropdownActionDropdown
          text="Rename & More…"
          content={
            <ProjectDropdown
              workspace={workspace}
              project={project}
              onRename={() => setIsRenaming(!isRenaming)}
            />
          }
        />
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
      forceHighlight={forceHighlight}
      additional={
        <TouchOnlyCollapsibleWrapper
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            setNestedOpen(!nestedOpen)
          }}
          disabled={!hasChildren}
        >
          <TouchOnlyCollapsibleIcon $open={nestedOpen} />
        </TouchOnlyCollapsibleWrapper>
      }
    />
  )

  return nestedProjects.length ? (
    <Collapsible.Root open={nestedOpen} onOpenChange={setNestedOpen}>
      {linkItem}
      <Collapsible.Content>
        <NestedProjectsWrapper level={depth + 1}>
          {nestedProjects.map(projectLink => (
            <ProjectListItem
              topLevelProject={topLevelProject}
              key={projectLink.project.identifier}
              project={projectLink.project}
              link={projectLink.link}
              nestedProjects={projectLink.nestedProjects}
              workspace={workspace}
              refToLast={refToLast}
              setEditedItem={setEditedItem}
              forceHighlight={forceHighlight}
              isProjectList={isProjectList}
              onOpenChange={onOpenChange}
              defaultOpen={defaultOpen}
              depth={depth + 1}
              isDescendantOf={isDescendantOf}
            />
          ))}
        </NestedProjectsWrapper>
      </Collapsible.Content>
    </Collapsible.Root>
  ) : (
    linkItem
  )
}
